import { Head, Link } from '@inertiajs/react'
import { useState } from 'react'
import { Copy } from 'lucide-react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import type { Breadcrumb } from '@/types/shared'

export type TeamPlayersIndexProps = {
  invitationUrl: string
  players: Array<{
    id: string
    name: string
    prefecture?: string | null
    positions: string[]
    updatedAt: string
  }>
  breadcrumbs: Breadcrumb[]
}

export default function TeamPlayersIndex({ invitationUrl, players, breadcrumbs }: TeamPlayersIndexProps) {
  const [copied, setCopied] = useState(false)

  const copyInvitationUrl = async () => {
    try {
      await navigator.clipboard.writeText(invitationUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('招待URLのコピーに失敗しました', error)
    }
  }

  return (
    <AppShell>
      <Head title="所属選手一覧" />
      <PageHeader
        title="所属選手一覧"
        description="所属選手のプロフィールを確認し、最新情報を把握しましょう。"
        breadcrumbs={breadcrumbs}
      />

      {players.length === 0 ? (
        <EmptyState
          title="現在、所属選手は登録されていません。"
          description="選手に招待URLを共有すると、このチームへの選手登録を開始できます。"
          action={
            <Button type="button" variant="secondary" onClick={copyInvitationUrl}>
              <Copy className="h-4 w-4" />
              {copied ? 'コピー済み' : '招待URLをコピー'}
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {players.map((player) => (
            <Link
              key={player.id}
              href={`/team/players/${player.id}`}
              className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white transition hover:border-white/20 hover:bg-white/10"
            >
              <p className="text-base font-semibold text-white">{player.name}</p>
              <p className="text-xs text-white/60 mt-1">
                {(player.prefecture ?? '所在地未登録') + ' · '}
                {player.positions.length ? player.positions.join(', ') : 'ポジション未設定'}
              </p>
              <p className="mt-3 text-[11px] text-white/40">{formatDate(player.updatedAt)} 更新</p>
            </Link>
          ))}
        </div>
      )}
    </AppShell>
  )
}

function formatDate(value: string) {
  return new Date(value).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })
}
